/*
 * Behaviour of the Pepper-style Cherry cart (markup: src/lib/render/pepper-cart.ts / the theme's
 * pepper-cart-drawer + main-cart sections, styles: pepper-cart.css). Reproduces wearpepper.com's cart drawer
 * (Dawn CartDrawer + Rebuy cart widget), measured in reference/pepper/cart/*:
 *  - open: `is-active` on the drawer -> visibility .3s ease + panel translateX(100%) -> 0 in .3s ease; page scroll
 *    locked; closes on the X, the overlay, Escape; `#cart-open` in the URL opens it on load
 *  - add to cart anywhere: the drawer content is swapped with the section returned by /cart/add.js, then it opens
 *  - quantity / remove: the line fades to .5 opacity until /cart/change.js answers, then the section is swapped
 *  - upsell "Add to cart": Adding... -> Added! for 2 s (the widget keeps its cards meanwhile), then refreshed
 *  - upsell carousel: one card per arrow click, wraps around (Flickity wrapAround)
 * Protocol: Impact's (theme.js) cart events, so every add-to-cart path of the store drives it:
 *  `cart:prepare-bundled-sections` (we add our section ids), `variant:add` (open), `cart:change` (re-render from
 *  `detail.cart.sections`), `cart:refresh` (re-fetch with the Section Rendering API). The header <cart-count>
 *  listens to the same `cart:change` event.
 */
(() => {
  "use strict";
  if (window.__pepperCart) return;
  window.__pepperCart = true;

  const ADDED_HOLD_MS = 2000;
  const root = () => window.Shopify?.routes?.root || "/";
  const drawer = () => document.querySelector('[data-ppc-root="drawer"]');
  const page = () => document.querySelector('[data-ppc-root="page"]');
  const sectionIds = () => [drawer(), page()].filter(Boolean).map((el) => el.dataset.sectionId);
  let lastFocus = null;
  let holdRecsUntil = 0;
  const rendered = new Map();

  /* ------------------------------------------------------------------ open / close */
  function open() {
    const el = drawer();
    if (!el || el.classList.contains("is-active")) return;
    lastFocus = document.activeElement;
    el.setAttribute("aria-hidden", "false");
    // force the hidden state to be computed before switching, so the panel slides in
    void el.offsetWidth;
    el.classList.add("is-active");
    document.documentElement.classList.add("ppc-lock");
    el.querySelector(".ppc-inner")?.focus({ preventScroll: true });
    syncCarousels();
  }

  function close() {
    const el = drawer();
    if (!el || !el.classList.contains("is-active")) return;
    el.classList.remove("is-active");
    el.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("ppc-lock");
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  /** Quick buy drawers (product cards) show their own "Added" panel in Impact; Pepper opens the cart instead. */
  function closeQuickBuyDrawers() {
    document.querySelectorAll("quick-buy-drawer[open], quick-buy-popover[open]").forEach((d) => {
      try {
        d.hide();
      } catch {
        /* not upgraded yet */
      }
    });
  }

  /* ------------------------------------------------------------------ rendering */
  function swap(target, fresh) {
    if (!target || !fresh) return;
    const heldRecs = Date.now() < holdRecsUntil ? target.querySelector("[data-ppc-recs]") : null;
    target.innerHTML = fresh.innerHTML;
    if (heldRecs) {
      const freshRecs = target.querySelector("[data-ppc-recs]");
      if (freshRecs) {
        freshRecs.replaceWith(heldRecs);
        const wait = holdRecsUntil - Date.now();
        setTimeout(() => {
          if (heldRecs.isConnected && Date.now() >= holdRecsUntil) heldRecs.replaceWith(freshRecs);
        }, wait);
      }
    }
  }

  /** Arrows only when the cards overflow (Flickity hides its buttons when everything fits). */
  function syncCarousels() {
    document.querySelectorAll(".ppc [data-ppc-carousel]").forEach((c) => {
      const track = c.querySelector("[data-ppc-track]");
      if (track) c.classList.toggle("is-static", track.scrollWidth <= track.clientWidth + 1);
    });
  }

  function render(sections) {
    if (!sections) return false;
    let done = false;
    const d = drawer();
    if (d && sections[d.dataset.sectionId] && rendered.get("drawer") !== sections[d.dataset.sectionId]) {
      const html = sections[d.dataset.sectionId];
      rendered.set("drawer", html);
      const fresh = new DOMParser().parseFromString(html, "text/html").querySelector('[data-ppc-root="drawer"]');
      if (fresh) {
        swap(d.querySelector(".ppc-inner"), fresh.querySelector(".ppc-inner"));
        d.classList.toggle("is-empty", fresh.classList.contains("is-empty"));
        done = true;
      }
    }
    const p = page();
    if (p && sections[p.dataset.sectionId] && rendered.get("page") !== sections[p.dataset.sectionId]) {
      const html = sections[p.dataset.sectionId];
      rendered.set("page", html);
      const fresh = new DOMParser().parseFromString(html, "text/html").querySelector('[data-ppc-root="page"]');
      if (fresh) {
        swap(p, fresh);
        p.classList.toggle("is-empty", fresh.classList.contains("is-empty"));
        done = true;
      }
    }
    if (done) syncCarousels();
    return done;
  }

  async function refresh() {
    const ids = sectionIds();
    if (!ids.length) return;
    const res = await fetch(`${location.pathname}?sections=${ids.join(",")}`, { headers: { Accept: "application/json" } });
    if (res.ok) render(await res.json());
  }

  /* ------------------------------------------------------------------ cart requests */
  function emitChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent, cart } }));
  }

  async function changeLine(line, quantity) {
    if (!line) return;
    line.classList.add("is-loading");
    line.querySelector(".ppc-item-error")?.remove();
    const res = await fetch(`${root()}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: line.dataset.ppcLine, quantity, sections: sectionIds(), sections_url: location.pathname }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      line.classList.remove("is-loading");
      const input = line.querySelector("[data-ppc-qty]");
      if (input) input.value = input.defaultValue;
      line.querySelector(".ppc-item-content")?.insertAdjacentHTML(
        "beforeend",
        `<p class="ppc-item-error" role="alert">${json.description || json.message || "This item could not be updated."}</p>`,
      );
      return;
    }
    emitChange(json, "line-item:change");
  }

  async function addUpsell(button) {
    const card = button.closest(".ppc-card");
    const select = card?.querySelector("[data-ppc-variant]");
    if (!select || button.disabled) return;
    const label = button.querySelector("span") || button;
    const initial = label.textContent;
    const labels = button.closest("[data-ppc-root]")?.dataset || {};
    button.disabled = true;
    label.textContent = labels.ppcAdding || "Adding...";
    try {
      const sections = sectionIds();
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const res = await fetch(`${root()}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items: [{ id: Number(select.value), quantity: 1 }], sections: [...new Set(sections)].join(","), sections_url: location.pathname }),
      });
      const json = await res.json();
      if (!res.ok) {
        label.textContent = initial;
        window.alert(json.description || json.message || "This item could not be added.");
        return;
      }
      const cart = await (await fetch(`${root()}cart.js`)).json();
      cart.sections = json.sections;
      label.textContent = labels.ppcAdded || "Added!";
      holdRecsUntil = Date.now() + ADDED_HOLD_MS;
      emitChange(cart, "variant:add");
      setTimeout(() => {
        label.textContent = initial;
        button.disabled = false;
      }, ADDED_HOLD_MS);
    } catch {
      label.textContent = initial;
      button.disabled = false;
    }
  }

  /* package protection (Pepper's Checkout+): CHECKOUT+ puts the protection product in the cart, "Checkout without
     package protection" takes it out, then both go to checkout. No protection product: straight to checkout. */
  async function checkout(button) {
    const id = Number(button.dataset.ppcProtectId || 0);
    const qty = Number(button.dataset.ppcProtectQty || 0);
    const box = button.closest(".ppc-ctas");
    if (box) box.setAttribute("aria-busy", "true");
    try {
      const json = { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" } };
      if (id && button.dataset.ppcGo === "plus" && qty === 0)
        await fetch(`${root()}cart/add.js`, { ...json, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      if (id && button.dataset.ppcGo === "without" && qty > 0)
        await fetch(`${root()}cart/update.js`, { ...json, body: JSON.stringify({ updates: { [id]: 0 } }) });
    } catch {
      /* checkout anyway */
    }
    window.location.href = `${root()}checkout`;
  }

  function moveCarousel(arrow) {
    const track = arrow.closest("[data-ppc-carousel]")?.querySelector("[data-ppc-track]");
    const card = track?.firstElementChild;
    if (!card) return;
    const dir = Number(arrow.dataset.ppcArrow);
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || "0");
    const max = track.scrollWidth - track.clientWidth;
    if (dir > 0 && track.scrollLeft >= max - 2) track.scrollTo({ left: 0 });
    else if (dir < 0 && track.scrollLeft <= 2) track.scrollTo({ left: max });
    else track.scrollBy({ left: dir * step });
  }

  /* ------------------------------------------------------------------ theme.js protocol */
  document.addEventListener("cart:prepare-bundled-sections", (event) => {
    const list = event.detail?.sections;
    if (!Array.isArray(list)) return;
    for (const id of sectionIds()) if (!list.includes(id)) list.push(id);
  });

  document.addEventListener("variant:add", (event) => {
    if (window.themeVariables?.settings?.cartType === "page") return;
    closeQuickBuyDrawers();
    const cart = event.detail?.cart;
    if (!render(cart?.sections)) refresh();
    open();
  });

  document.addEventListener("cart:change", (event) => {
    const cart = event.detail?.cart;
    if (!cart) return;
    if (!render(cart.sections) && !cart.sections) refresh();
  });

  document.addEventListener("cart:refresh", () => refresh());
  window.addEventListener("pageshow", (event) => event.persisted && refresh());

  /* ------------------------------------------------------------------ UI */
  document.addEventListener(
    "click",
    (event) => {
      const t = event.target instanceof Element ? event.target : null;
      if (!t) return;
      const opener = t.closest('[aria-controls="cart-drawer"]');
      if (opener && drawer() && !opener.closest(".ppc")) {
        event.preventDefault();
        event.stopPropagation();
        open();
        return;
      }
      if (t.closest("[data-ppc-close]")) {
        close();
        return;
      }
      const step = t.closest("[data-ppc-step]");
      if (step) {
        const line = step.closest("[data-ppc-line]");
        const input = line?.querySelector("[data-ppc-qty]");
        if (input) changeLine(line, Math.max(0, parseInt(input.value || "0", 10) + Number(step.dataset.ppcStep)));
        return;
      }
      const remove = t.closest("[data-ppc-remove]");
      if (remove) {
        changeLine(remove.closest("[data-ppc-line]"), 0);
        return;
      }
      const add = t.closest("[data-ppc-add]");
      if (add) {
        addUpsell(add);
        return;
      }
      const arrow = t.closest("[data-ppc-arrow]");
      if (arrow) moveCarousel(arrow);
      const go = t.closest("[data-ppc-go]");
      if (go) {
        event.preventDefault();
        checkout(go);
      }
    },
    true,
  );

  document.addEventListener("change", (event) => {
    const t = event.target;
    if (!(t instanceof Element) || !t.closest(".ppc")) return;
    if (t.matches("[data-ppc-qty]")) {
      const quantity = parseInt(t.value, 10);
      if (Number.isFinite(quantity) && quantity >= 0) changeLine(t.closest("[data-ppc-line]"), quantity);
      else t.value = t.defaultValue;
    } else if (t.matches("[data-ppc-variant]")) {
      const option = t.selectedOptions[0];
      const price = t.closest(".ppc-card")?.querySelector("[data-ppc-card-price]");
      if (option && price)
        price.innerHTML = `<span>${option.dataset.price}</span>${option.dataset.compare ? `<s>${option.dataset.compare}</s>` : ""}`;
    } else if (t.matches("[data-ppc-note]")) {
      fetch(`${root()}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: t.value }),
        keepalive: true,
      });
    }
  });

  document.addEventListener("keydown", (event) => {
    const el = drawer();
    if (!el?.classList.contains("is-active")) return;
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;
    // focus trap inside the open drawer
    const focusables = [...el.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (f) => f.offsetParent !== null,
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const boot = () => {
    syncCarousels();
    if (location.hash === "#cart-open") open();
  };
  window.addEventListener("resize", syncCarousels);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  window.addEventListener("hashchange", boot);

  window.PepperCart = { open, close, refresh };
})();
